import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import { IOnInventoryAuditDTO } from "../models/DTO/IInventoryAuditDTO";
import IOnGetReservedOrdersDTO from "../models/DTO/IOnGetReservedOrdersDTO";
import IOnGetBackorderedOrdersDTO from "../models/DTO/IOnGetBackorderedOrdersDTO";
import IGetSingleOrderBackorderAnalysisDTO from "../models/DTO/IGetSingleOrderBackorderAnalysisDTO";
import IOnGetSingleOrderBackorderAnalysisDTO from "../models/DTO/IOnGetSingleOrderBackorderAnalysisDTO";
import InventoryAllocationStrategyEnum from "../models/DTO/InventoryAllocationStrategyEnum";
import { IFulfillBackordersByOrderDTO, IFulfillBackordersByOrderResultDTO } from "../models/DTO/IFulfillBackordersByOrderDTO";
import { IInventoryResetDTO, IOnInventoryResetDTO } from "../models/DTO/IInventoryResetDTO";

interface InventoryAuditApiResponse {
    success: boolean;
    message?: string;
    data?: IOnInventoryAuditDTO;
}

/**
 * Audit reserved quantity on hand for discrepancies
 * Checks catalog items for inventory reservation discrepancies without fixing them
 */
const auditInventory = async (
    catalogGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryAuditApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryAudit,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ catalogGuid }),
    });

    if (!response.ok) {
        throw new Error(`Failed to audit inventory: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Audit and fixreserved quantity on hand for discrepancies
 * Checks catalog items for inventory reservation discrepancies without fixing them
 */
const auditAndFixInventory = async (
    catalogGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryAuditApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryAuditFix,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ catalogGuid }),
    });

    if (!response.ok) {
        throw new Error(`Failed to audit inventory: ${response.statusText}`);
    }

    return response.json();
};

interface GetReservedOrdersParams {
    catalogItemGuid: string;
    limit?: number;
    offset?: number;
}

interface GetReservedOrdersApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetReservedOrdersDTO;
    totalCount?: number;
}

interface GetReservedOrdersCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Get paginated list of orders with reserved quantity for a catalog item
 */
const getReservedOrders = async (
    params: GetReservedOrdersParams,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetReservedOrdersApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryReservedOrders,
        params: { catalogItemGuid: params.catalogItemGuid },
        query: {
            limit: params.limit || 25,
            page: params.offset || 0,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get reserved orders: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Export all orders with reserved quantity as CSV
 */
const getReservedOrdersCsv = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetReservedOrdersCsvApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryReservedOrdersCsv,
        params: { catalogItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export reserved orders: ${response.statusText}`);
    }

    return response.json();
};

interface GetBackorderedOrdersParams {
    catalogItemGuid: string;
    limit?: number;
    offset?: number;
}

interface GetBackorderedOrdersApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetBackorderedOrdersDTO;
    totalCount?: number;
}

interface GetBackorderedOrdersCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Get paginated list of orders with backordered quantity for a catalog item
 */
const getBackorderedOrders = async (
    params: GetBackorderedOrdersParams,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetBackorderedOrdersApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryBackorderedOrders,
        params: { catalogItemGuid: params.catalogItemGuid },
        query: {
            limit: params.limit || 25,
            page: params.offset || 0,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get backordered orders: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Export all orders with backordered quantity as CSV
 */
const getBackorderedOrdersCsv = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetBackorderedOrdersCsvApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryBackorderedOrdersCsv,
        params: { catalogItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export backordered orders: ${response.statusText}`);
    }

    return response.json();
};

interface GetSingleOrderBackorderAnalysisApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetSingleOrderBackorderAnalysisDTO;
}

/**
 * Get backorder analysis for a single order
 * Analyzes fulfillment potential for a specific order's backordered items
 */
const getSingleOrderBackorderAnalysis = async (
    params: IGetSingleOrderBackorderAnalysisDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetSingleOrderBackorderAnalysisApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventorySingleOrderBackorderAnalysis,
        params: { orderGuid: params.orderGuid },
        query: {
            allocationStrategy: params.allocationStrategy || InventoryAllocationStrategyEnum.FIFO,
            considerReservedInventory: params.considerReservedInventory || false,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get backorder analysis: ${response.statusText}`);
    }

    return response.json();
};

interface FulfillBackordersByOrderApiResponse {
    success: boolean;
    message?: string;
    data?: IFulfillBackordersByOrderResultDTO;
}

/**
 * Fulfill backorders for specific orders
 * Allocates inventory to pending order items based on allocation strategy
 */
const fulfillBackordersByOrder = async (
    payload: IFulfillBackordersByOrderDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FulfillBackordersByOrderApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryFulfillBackordersByOrder,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to fulfill backorders: ${response.statusText}`);
    }

    return response.json();
};

interface InventoryResetApiResponse extends IOnInventoryResetDTO {}

/**
 * Reset inventory reservations for a catalog
 * Implements the "Reconstruct & Pour" strategy
 */
const resetInventory = async (
    payload: IInventoryResetDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryResetApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryReset,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to reset inventory: ${response.statusText}`);
    }

    return response.json();
};

const InventoryService = {
    auditInventory,
    auditAndFixInventory,
    getReservedOrders,
    getReservedOrdersCsv,
    getBackorderedOrders,
    getBackorderedOrdersCsv,
    getSingleOrderBackorderAnalysis,
    fulfillBackordersByOrder,
    resetInventory,
};

export default InventoryService;
